import { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, X, Download } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

/**
 * Two-step Docket / CSV / XLSX importer.
 *
 *  1. Drop file  → POST /customers/import/preview → show summary + preview table.
 *  2. Confirm    → POST /customers/import/commit  → persist, capture batch_id.
 *  3. Optional   → "Undo import" button (rollback endpoint) for 5 min after commit.
 */
export function ImportCustomersDialog({ open, setOpen, onImported }) {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [lastBatch, setLastBatch] = useState(null);
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setPreviewData(null);
        setCommitting(false);
        setLastBatch(null);
    };

    const handleFile = async (f) => {
        if (!f) return;
        const ok = /\.(csv|xlsx|xlsm)$/i.test(f.name);
        if (!ok) {
            toast.error("Only .csv, .xlsx or .xlsm files supported");
            return;
        }
        setFile(f);
        setPreviewData(null);
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await api.post("/customers/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreviewData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Preview failed");
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
    };

    const commit = async () => {
        if (!file) return;
        setCommitting(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/customers/import/commit", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLastBatch(data);
            toast.success(
                `Imported ${data.created} customers${
                    data.invoices_created ? ` + ${data.invoices_created} carry-over invoices` : ""
                }`
            );
            onImported?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        } finally {
            setCommitting(false);
        }
    };

    const rollback = async () => {
        if (!lastBatch?.batch_id) return;
        if (!window.confirm(`Undo the import of ${lastBatch.created} customers? This cannot be undone.`)) return;
        try {
            const { data } = await api.post(`/customers/import/rollback/${lastBatch.batch_id}`);
            toast.success(`Rolled back — removed ${data.customers_deleted} customers, ${data.invoices_deleted} invoices`);
            setLastBatch(null);
            reset();
            setOpen(false);
            onImported?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rollback failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="import-customers-btn">
                    <Upload className="w-4 h-4 mr-1" />Import CSV/Excel
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl" data-testid="import-customers-dialog">
                <DialogHeader>
                    <DialogTitle>Import customers</DialogTitle>
                    <DialogDescription>
                        Drop a Docket export (.csv) or any spreadsheet (.xlsx). We'll preview
                        before writing anything.
                    </DialogDescription>
                    {!file && !lastBatch && (
                        <div className="pt-2">
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/customers/import/template`}
                                download="wastehq-customer-import-template.csv"
                                data-testid="import-template-download"
                                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                            >
                                <Download className="w-3.5 h-3.5" />
                                Download the CSV template
                            </a>
                            <span className="text-xs text-muted-foreground ml-2">
                                — headers we recognize, plus 4 example rows (cart, 2yd, 4yd, carry-over balance)
                            </span>
                        </div>
                    )}
                </DialogHeader>

                {!lastBatch && (
                    <>
                        {/* Drop zone */}
                        {!file && (
                            <div
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={onDrop}
                                onClick={() => inputRef.current?.click()}
                                data-testid="import-drop-zone"
                                className="border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors"
                            >
                                <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                                <div className="text-sm font-medium">Drop file here or click to browse</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    Accepts .csv, .xlsx — max 5,000 rows
                                </div>
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".csv,.xlsx,.xlsm"
                                    className="hidden"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    data-testid="import-file-input"
                                />
                            </div>
                        )}

                        {/* Preview */}
                        {previewData && (
                            <div className="space-y-3" data-testid="import-preview">
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="font-mono text-xs bg-secondary px-2 py-0.5 rounded">
                                        {file.name}
                                    </span>
                                    <button onClick={reset} className="text-muted-foreground hover:text-foreground">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {previewData.format !== "docket" && (
                                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900 rounded text-xs">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="font-semibold text-amber-800 dark:text-amber-300">Format not recognized</div>
                                            <div className="text-amber-700 dark:text-amber-400 mt-0.5">{previewData.warnings?.join(" · ")}</div>
                                        </div>
                                    </div>
                                )}

                                {previewData.format === "docket" && (
                                    <>
                                        <div className="grid grid-cols-4 gap-3">
                                            <StatCard label="Rows in file" value={previewData.total_rows} />
                                            <StatCard label="Unique customers" value={previewData.unique_customers} />
                                            <StatCard label="New" value={previewData.new_count} accent="green" />
                                            <StatCard label="Duplicates" value={previewData.duplicate_count} accent={previewData.duplicate_count > 0 ? "amber" : "muted"} />
                                        </div>

                                        {previewData.warnings?.length > 0 && (
                                            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900 rounded text-xs">
                                                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                                <div className="text-amber-800 dark:text-amber-300">
                                                    {previewData.warnings.join(" · ")}
                                                </div>
                                            </div>
                                        )}

                                        <div className="border border-border rounded overflow-x-auto max-h-80" data-testid="import-preview-table">
                                            <table className="w-full text-xs">
                                                <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                                                    <tr>
                                                        <th className="text-left px-3 py-2 overline">Customer</th>
                                                        <th className="text-left px-3 py-2 overline">Carts</th>
                                                        <th className="text-left px-3 py-2 overline">Day</th>
                                                        <th className="text-right px-3 py-2 overline">Quarterly</th>
                                                        <th className="text-right px-3 py-2 overline">Balance</th>
                                                        <th className="text-left px-3 py-2 overline">Next Bill</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                {previewData.customers.map((c, i) => (
                                                    <tr key={i} className="border-t border-border">
                                                        <td className="px-3 py-2">
                                                            <div className="font-medium">{c.name}</div>
                                                            <div className="text-[10px] text-muted-foreground">{c.address}</div>
                                                            {c.email && <div className="text-[10px] text-muted-foreground">{c.email}</div>}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {c.type === "residential_dumpster" ? (
                                                                <span className="text-blue-700 dark:text-blue-400 font-semibold">
                                                                    {c.dumpster_size || "dumpster"}
                                                                </span>
                                                            ) : (
                                                                <>{c.cart_quantity}× {c.cart_size}</>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 capitalize">{c.route_day || "monday"}</td>
                                                        <td className="px-3 py-2 text-right tabular">
                                                            {fmtMoney(c.monthly_rate)}
                                                            <div className="text-[9px] text-muted-foreground capitalize">
                                                                {c.billing_frequency || "quarterly"}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular">
                                                            {c.carry_over_balance > 0 ? (
                                                                <span className="text-amber-600 dark:text-amber-400 font-semibold">
                                                                    {fmtMoney(c.carry_over_balance)}
                                                                </span>
                                                            ) : <span className="text-muted-foreground">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2 text-muted-foreground">
                                                            {c.next_billing_date ? new Date(c.next_billing_date).toLocaleDateString() : "—"}
                                                            {c.is_duplicate && (
                                                                <span className="ml-2 text-[10px] uppercase text-amber-600">dup</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Success screen */}
                {lastBatch && (
                    <div className="space-y-3 py-4" data-testid="import-success">
                        <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                            <div>
                                <div className="font-heading text-lg font-semibold">Import complete</div>
                                <div className="text-sm text-muted-foreground">
                                    {lastBatch.created} customers added
                                    {lastBatch.with_balance_flagged > 0 && ` · ${lastBatch.with_balance_flagged} flagged with open balance`}
                                    {lastBatch.skipped > 0 && ` · ${lastBatch.skipped} skipped (duplicates)`}
                                </div>
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground bg-secondary/50 p-3 rounded">
                            Draft quarterly invoices auto-generate 3 months after each customer's last Docket
                            invoice date. Drafts sit in the Pending Review queue for 24 hours so you can add
                            extra fees before the 7-day heads-up email + SMS fires.
                        </div>
                        <div className="text-xs font-mono text-muted-foreground">
                            Batch ID: {lastBatch.batch_id}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {lastBatch ? (
                        <>
                            <Button variant="outline" onClick={rollback} data-testid="import-rollback-btn">
                                Undo import
                            </Button>
                            <Button onClick={() => { reset(); setOpen(false); }} data-testid="import-done-btn">
                                Done
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button
                                onClick={commit}
                                disabled={!previewData || previewData.format !== "docket" || previewData.new_count === 0 || committing}
                                data-testid="import-commit-btn"
                            >
                                {committing ? "Importing…" :
                                    previewData?.new_count > 0
                                        ? `Import ${previewData.new_count} customer${previewData.new_count === 1 ? "" : "s"}`
                                        : "Import"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StatCard({ label, value, accent = "muted" }) {
    const tones = {
        muted: "text-foreground",
        green: "text-emerald-600 dark:text-emerald-400",
        amber: "text-amber-600 dark:text-amber-400",
    };
    return (
        <div className="surface border border-border rounded p-3">
            <div className="overline text-[10px]">{label}</div>
            <div className={`font-heading text-2xl font-bold tabular mt-1 ${tones[accent]}`}>{value}</div>
        </div>
    );
}
